import { useRef, useState } from 'react';
import { FileText, Maximize2, Minimize2 } from 'lucide-react';
import {
  DAY_SIGNATURE_ROLES,
  signatureRoleLabel,
  type PortalDayLog,
  type SignAssemblyDayInput,
} from '@proerp/shared';
import { Button, Dialog, DialogSurface, Input, SignaturePad, type SignaturePadHandle } from '@proerp/ui';
import { DaySignatureSummary } from './DaySignatureSummary';

/**
 * Unterschrift zum Bau-Tagesbericht im Partnerportal (ADR 0074 + ADR 0087).
 *
 * **Zusammenfassung vor der Unterschrift** (Tech-Lead 2026-08-10): Vorher
 * bekam der Bauherr ein leeres Feld gereicht und zeichnete blind gegen. Eine so
 * geleistete Unterschrift ist im Streitfall angreifbar — der Tagesbericht ist
 * ein Beweisdokument. Jetzt steht über dem Feld im Klartext, was er bestätigt,
 * und daneben der Weg zum vollständigen PDF.
 *
 * **Vergrößern** ist der Moment der Übergabe: Der Monteur füllt Rolle, Name und
 * Ort aus, tippt auf „Vergrößern" und reicht das Gerät weiter. Dann stehen
 * links die Angaben zum Lesen und rechts das Feld zum Unterschreiben — beides
 * gleichzeitig, ohne Formularfelder ringsum. Ein bereits gezogener Schriftzug
 * übersteht den Wechsel.
 *
 * **Zur Auswahl stehen nur noch offene Rollen** (`openRoles`): Der Server nimmt
 * je Rolle genau eine Unterschrift und würde eine vorhandene überschreiben —
 * das ist ein Korrekturweg für uns, nicht etwas, das auf der Baustelle
 * versehentlich passieren darf.
 */
export function DaySignatureDialog({
  isOpen,
  onClose,
  day,
  dayLabel,
  taskTitle,
  suggestedName,
  suggestedPlace,
  openRoles = DAY_SIGNATURE_ROLES,
  isPending,
  onSign,
  onOpenPdf,
}: {
  isOpen: boolean;
  onClose: () => void;
  /** Der Tag, der unterschrieben wird — Grundlage der Zusammenfassung. */
  day: PortalDayLog;
  /** Datum des Tages, zur Kontrolle im Kopf. */
  dayLabel: string;
  taskTitle: string;
  suggestedName?: string;
  suggestedPlace?: string;
  /** Rollen, die noch nicht unterschrieben haben. */
  openRoles?: readonly { value: string; label: string }[];
  isPending: boolean;
  onSign: (input: SignAssemblyDayInput) => void;
  /** Öffnet den vollständigen Tagesbericht als PDF. */
  onOpenPdf: () => void;
}) {
  const pad = useRef<SignaturePadHandle>(null);
  const [role, setRole] = useState<string>(openRoles[0]?.value ?? '');
  const [signerName, setSignerName] = useState('');
  // Unterschrieben wird auf der Baustelle — der Ort steht schon fest.
  const [place, setPlace] = useState(suggestedPlace ?? '');
  const [hasStrokes, setHasStrokes] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isMaximized, setIsMaximized] = useState(false);

  const roleLabel = signatureRoleLabel(role);
  const signedRoles = day.signatures.map(
    (signature) => `${signatureRoleLabel(signature.role)}: ${signature.signerName}`,
  );

  function pickRole(next: string) {
    setRole(next);
    if (!signerName.trim() && next === 'BAULEITER' && suggestedName) setSignerName(suggestedName);
  }

  function submit() {
    const image = pad.current?.toDataUrl();
    if (!image) {
      setError('Bitte im Feld unterschreiben.');
      return;
    }
    if (signerName.trim().length < 2) {
      setError('Bitte den Namen der unterschreibenden Person angeben.');
      return;
    }
    setError(null);
    onSign({ role, signerName: signerName.trim(), place: place.trim() || null, image });
  }

  // Haben beide Rollen gezeichnet, gibt es hier nichts mehr zu tun. Der
  // Aufrufer zeigt den Knopf dann gar nicht erst — das hier ist die zweite Naht.
  if (!isOpen || openRoles.length === 0) return null;

  const summary = (
    <DaySignatureSummary day={day} dayLabel={dayLabel} taskTitle={taskTitle} />
  );

  const pdfLink = (
    <button
      type="button"
      onClick={onOpenPdf}
      className="inline-flex cursor-pointer items-center gap-1.5 text-sm text-accent-600 transition-colors hover:underline"
    >
      <FileText className="size-4" aria-hidden />
      Vollständigen Tagesbericht als PDF ansehen
    </button>
  );

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      {/* Ein einziger DialogSurface für beide Größen: Würde zwischen zwei
          Komponenten umgeschaltet, baute React das Unterschriftsfeld neu auf
          und der Schriftzug wäre weg. Nur Klassen ändern sich. Der Titel ist
          visuell versteckt — sichtbar steht er samt Datum im Kopf, und beides
          zugleich hieße, es zweimal im Baum zu haben (auch für Screenreader). */}
      <DialogSurface
        title="Tagesbericht unterschreiben"
        visuallyHiddenTitle
        className={
          isMaximized
            ? 'top-0 left-0 h-[100dvh] max-h-none w-screen max-w-none translate-x-0 translate-y-0 rounded-none'
            : 'max-h-[calc(100dvh-2rem)] w-[min(680px,94vw)] max-w-none overflow-y-auto'
        }
      >
        <div className="flex h-full flex-col gap-4 p-5">
          <div className="flex flex-wrap items-start justify-between gap-2">
            {/* Nur der Titel: Welcher Tag es ist, steht in der Zusammenfassung
                — dort, wo es zum unterschriebenen Inhalt gehört. Zweimal wäre
                es Rauschen (und für Screenreader doppelt). */}
            <h2 className="text-base font-semibold text-foreground">Tagesbericht unterschreiben</h2>
            <button
              type="button"
              onClick={() => setIsMaximized((value) => !value)}
              className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-sm transition-colors hover:bg-muted"
            >
              {isMaximized ? (
                <Minimize2 className="size-4" aria-hidden />
              ) : (
                <Maximize2 className="size-4" aria-hidden />
              )}
              {isMaximized ? 'Verkleinern' : 'Gerät übergeben'}
            </button>
          </div>

          {isMaximized ? (
            /* Übergabe-Ansicht: links lesen, rechts unterschreiben — beides
               gleichzeitig sichtbar, damit niemand zwischen zwei Schritten
               hin- und herschalten muss, während er das Gerät hält. */
            <div className="grid min-h-0 flex-1 gap-4 md:grid-cols-2">
              <div className="min-h-0 overflow-y-auto">
                {summary}
                <div className="mt-3">{pdfLink}</div>
              </div>
              <div className="flex min-h-0 flex-col gap-2">
                <p className="text-sm text-muted-foreground">
                  Unterschrift <strong className="text-foreground">{roleLabel}</strong>
                  {signerName.trim() && ` · ${signerName.trim()}`}
                </p>
                <div className="min-h-0 flex-1">
                  <SignaturePad ref={pad} height="fill" aria-label="Unterschriftsfeld" onChange={setHasStrokes} />
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="grid gap-3 sm:grid-cols-3">
                <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                  Rolle
                  <select
                    value={role}
                    onChange={(event) => pickRole(event.target.value)}
                    className="h-9 rounded-lg border border-border bg-background px-2 text-sm text-foreground"
                  >
                    {openRoles.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                  Name
                  <Input
                    value={signerName}
                    onChange={(event) => setSignerName(event.target.value)}
                    placeholder="Vor- und Nachname"
                  />
                </label>
                <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                  Ort
                  <Input value={place} onChange={(event) => setPlace(event.target.value)} />
                </label>
              </div>

              {summary}
              {pdfLink}

              <div>
                <p className="mb-1.5 text-sm text-muted-foreground">
                  Unterschrift <strong className="text-foreground">{roleLabel}</strong>
                  {signerName.trim() && ` · ${signerName.trim()}`}
                </p>
                <SignaturePad ref={pad} height={200} aria-label="Unterschriftsfeld" onChange={setHasStrokes} />
              </div>
            </>
          )}

          {/* Nach der ersten Unterschrift ist der Tag längst gesperrt — dann
              wäre „wird gesperrt" schlicht falsch, und wer die zweite
              Unterschrift leistet, soll wissen, dass er dasselbe bestätigt. */}
          <p className="shrink-0 rounded-lg border border-warning-200 bg-warning-50 px-3 py-2 text-sm text-warning-900 dark:border-warning-900/50 dark:bg-warning-950/40 dark:text-warning-100">
            {day.lockedAt ? (
              <>
                Dieser Tag ist bereits <strong>gesperrt</strong>
                {signedRoles.length > 0 && ` (${signedRoles.join(' · ')})`}. Deine Unterschrift kommt
                hinzu; am Inhalt ändert sich nichts.
              </>
            ) : (
              <>
                Mit der Unterschrift wird dieser Tag <strong>gesperrt</strong> und lässt sich nicht
                mehr ändern. Ein gegengezeichneter Tagesbericht ist ein Beweisdokument.
              </>
            )}
          </p>

          {error && (
            <p role="alert" className="shrink-0 text-sm text-danger-600">
              {error}
            </p>
          )}

          <div className="flex shrink-0 flex-wrap justify-end gap-2">
            <Button variant="ghost" type="button" onClick={onClose}>
              Abbrechen
            </Button>
            <Button
              variant="primary"
              type="button"
              onClick={submit}
              isLoading={isPending}
              disabled={!hasStrokes}
            >
              Unterschrift speichern
            </Button>
          </div>
        </div>
      </DialogSurface>
    </Dialog>
  );
}
